import { type } from 'arktype';

// ---------------------------------------------------------------------------
// Retry policy (JSONB stored in webhook_endpoints.retry_policy)
// ---------------------------------------------------------------------------

export const retryPolicySchema = type({
  'maxAttempts?': type('number > 0').configure({
    description: 'Maximum number of delivery attempts before the delivery is considered failed.',
    examples: [5],
  }),
  'backoffType?': type("'exponential' | 'fixed'").configure({
    description: "Backoff strategy: 'exponential' multiplies the delay on each retry, 'fixed' keeps it constant.",
    examples: ['exponential'],
  }),
  'initialDelayMs?': type('number > 0').configure({
    description: 'Delay in milliseconds before the first retry attempt.',
    examples: [1000],
  }),
  'maxDelayMs?': type('number > 0').configure({
    description: 'Upper bound for any single retry delay, in milliseconds.',
    examples: [300000],
  }),
  'backoffMultiplier?': type('number > 0').configure({
    description: 'Multiplier applied to the previous delay when using exponential backoff.',
    examples: [2],
  }),
}).configure({ description: 'Retry policy for webhook delivery. All fields are optional and fall back to tenant defaults.' });

export type RetryPolicy = typeof retryPolicySchema.t;

export const DEFAULT_RETRY_POLICY: Required<RetryPolicy> = {
  maxAttempts: 5,
  backoffType: 'exponential',
  initialDelayMs: 1000,
  maxDelayMs: 300000,
  backoffMultiplier: 2,
};

// ---------------------------------------------------------------------------
// Create webhook endpoint
// ---------------------------------------------------------------------------

export const createWebhookEndpointSchema = type({
  name: type('string >= 1').configure({
    description: 'Human-readable name for this endpoint shown in admin UI.',
    examples: ['Payroll export'],
  }),
  url: type('string >= 1').configure({
    description: 'HTTPS URL that will receive webhook POST requests.',
    examples: ['https://api.example.com/smartcapacity/webhooks'],
  }),
  'secret?': type('string >= 1').configure({
    description:
      'Optional HMAC-SHA256 signing secret. The server generates one when omitted. Deliveries carry the signature in `X-Webhook-Signature-256`.',
    examples: ['whsec_xxxxxxxxxxxxxxxx'],
  }),
  event_types: type('string[] >= 1').configure({
    description: "List of event types this endpoint subscribes to. Use ['*'] to match all events.",
    examples: [['calendar_slot_created', 'calendar_slot_updated']],
  }),
  'is_active?': type('boolean').configure({
    description: 'Whether deliveries are attempted. Defaults to `true` when omitted.',
  }),
  'custom_headers?': type('Record<string, string> | null').configure({
    description: 'Extra HTTP headers sent on every request. `null` clears previously stored headers.',
  }),
  'retry_policy?': retryPolicySchema.or('null').configure({
    description: 'Per-endpoint retry policy overrides. `null` falls back to the tenant defaults.',
  }),
}).configure({ description: 'Payload for registering a new webhook endpoint.' });

export type CreateWebhookEndpoint = typeof createWebhookEndpointSchema.t;

// ---------------------------------------------------------------------------
// Test delivery
// ---------------------------------------------------------------------------

export const testWebhookDeliverySchema = type({
  'event_type?': type('string >= 1').configure({
    description: 'Event type placed in the delivery envelope. Defaults to `webhook_test`.',
    examples: ['calendar_slot_created'],
  }),
  'payload?': type('Record<string, unknown>').configure({
    description: 'Object delivered as `data.payload` of the envelope. Defaults to `{}`.',
  }),
}).configure({ description: 'Optional body of a webhook test delivery. An empty body sends `webhook_test` with an empty payload.' });

export type TestWebhookDelivery = typeof testWebhookDeliverySchema.t;

// ---------------------------------------------------------------------------
// Update webhook endpoint
// ---------------------------------------------------------------------------

export const updateWebhookEndpointSchema = type({
  'name?': type('string >= 1').configure({ description: 'Updated display name.', examples: ['Payroll export'] }),
  'url?': type('string >= 1').configure({
    description: 'Updated HTTPS URL.',
    examples: ['https://api.example.com/smartcapacity/webhooks'],
  }),
  'event_types?': type('string[] >= 1').configure({
    description: "Updated list of subscribed event types. Use ['*'] to match all events.",
    examples: [['calendar_slot_created']],
  }),
  'is_active?': type('boolean').configure({
    description: 'Activate (`true`) or pause (`false`) deliveries to this endpoint.',
  }),
  'custom_headers?': type('Record<string, string> | null').configure({
    description: 'Updated custom headers. `null` clears all custom headers.',
  }),
  'retry_policy?': retryPolicySchema.or('null').configure({
    description: 'Updated retry policy. `null` reverts to tenant defaults.',
  }),
}).configure({ description: 'Partial update for a webhook endpoint. Fields omitted remain unchanged.' });

export type UpdateWebhookEndpoint = typeof updateWebhookEndpointSchema.t;

// ---------------------------------------------------------------------------
// Webhook payload envelope (sent to external endpoints)
// ---------------------------------------------------------------------------

export interface WebhookPayloadEnvelope {
  id: string;
  event: string;
  timestamp: string;
  version: '1.0';
  data: {
    notificationId: string | null;
    title: string | null;
    message: string | null;
    category: string | null;
    priority: string | null;
    metadata: Record<string, unknown> | null;
    payload: Record<string, unknown>;
  };
}

// ---------------------------------------------------------------------------
// Webhook endpoint response (secret masked)
// ---------------------------------------------------------------------------

export interface WebhookEndpointResponse {
  id: string;
  name: string;
  url: string;
  secret: string;
  eventTypes: string[];
  isActive: boolean;
  consecutiveFailures: number;
  customHeaders: Record<string, string> | null;
  retryPolicy: RetryPolicy;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Webhook delivery log response
// ---------------------------------------------------------------------------

export interface WebhookDeliveryLogResponse {
  id: string;
  deliveryId: string | null;
  endpointId: string;
  requestUrl: string;
  requestHeaders: Record<string, string> | null;
  requestBody: Record<string, unknown> | null;
  responseStatus: number | null;
  responseHeaders: Record<string, string> | null;
  responseBody: string | null;
  durationMs: number | null;
  attemptedAt: string;
}

// ---------------------------------------------------------------------------
// Circuit breaker constants
// ---------------------------------------------------------------------------

/** Default number of consecutive failures before auto-disabling an endpoint. */
export const DEFAULT_CIRCUIT_BREAKER_THRESHOLD = 10;

/** Maximum response body size (bytes) to store in delivery logs. */
export const MAX_RESPONSE_BODY_SIZE = 64 * 1024; // 64KB

/** Webhook HTTP request timeout in milliseconds. */
export const WEBHOOK_REQUEST_TIMEOUT_MS = 30_000;

/** User-Agent header sent with webhook requests. */
export const WEBHOOK_USER_AGENT = 'SmartCapacity-Webhook/1.0';

/** Webhook payload version. */
export const WEBHOOK_PAYLOAD_VERSION = '1.0' as const;

// ---------------------------------------------------------------------------
// Calendar slot event payload (`data.payload` of calendar_slot_* deliveries)
// ---------------------------------------------------------------------------

export const calendarSlotEventPayloadSchema = type({
  calendarId: type('string').configure({
    description: 'Calendar the slot belongs to.',
    examples: ['cal_abc123'],
  }),
  slotId: type('string').configure({
    description: 'Calendar slot id.',
    examples: ['slot_abc123'],
  }),
  dateFrom: type('string').configure({
    description: 'First date of the slot (YYYY-MM-DD).',
    examples: ['2026-10-14'],
  }),
  dateTo: type('string | null').configure({
    description: 'Last date of the slot (YYYY-MM-DD), or null.',
  }),
  recurrenceRule: type('string | null').configure({
    description: 'Bare RRULE without the `RRULE:` prefix, or null for a single slot.',
    examples: ['FREQ=WEEKLY;BYDAY=MO'],
  }),
  data: type('object | null').configure({
    description: 'Slot data: type, times and custom properties. For deleted events, the last state before deletion.',
  }),
  onlineMeeting: type({ provider: "'teams'", joinUrl: 'string' }).or('null').configure({
    description: 'Verified Teams join link, or null while none has been verified yet.',
  }),
}).configure({
  description: 'Payload of calendar_slot_created, calendar_slot_updated and calendar_slot_deleted deliveries (`data.payload`).',
});
